import { Badge, type BadgeTone } from '@/components/ui/badge'
import type {
  ApplicationState,
  ContestEntryState,
  ContestStatus,
  ContractState,
  CourseStatus,
  JobStatus,
  MilestoneState,
  OrderState,
  PayoutState,
  ProposalStatus,
  ServiceStatus,
  VacancyStatus,
} from '@/lib/types'

/**
 * Terjemahan keadaan domain menjadi lencana yang dapat dibaca pengguna.
 *
 * Seluruh label ada di satu berkas supaya kata yang dipakai untuk satu
 * keadaan selalu sama di mana pun ia muncul — daftar, rincian, maupun
 * notifikasi. Status yang berganti nama antar halaman membuat pengguna
 * mengira ada dua hal berbeda.
 */

type Entry = { label: string; tone: BadgeTone }

const courseStatus: Record<CourseStatus, Entry> = {
  draft: { label: 'Draf', tone: 'neutral' },
  in_review: { label: 'Menunggu tinjauan', tone: 'info' },
  published: { label: 'Terbit', tone: 'success' },
  archived: { label: 'Diarsipkan', tone: 'neutral' },
}

const orderState: Record<OrderState, Entry> = {
  pending: { label: 'Menunggu pembayaran', tone: 'warning' },
  paid: { label: 'Lunas', tone: 'success' },
  expired: { label: 'Kedaluwarsa', tone: 'neutral' },
  failed: { label: 'Gagal', tone: 'danger' },
  refunded: { label: 'Dikembalikan', tone: 'info' },
  partial_refund: { label: 'Refund sebagian', tone: 'info' },
  chargeback: { label: 'Chargeback', tone: 'danger' },
}

const milestoneState: Record<MilestoneState, Entry> = {
  pending: { label: 'Menunggu dana', tone: 'neutral' },
  funded: { label: 'Dana terkunci', tone: 'info' },
  in_progress: { label: 'Sedang dikerjakan', tone: 'brand' },
  submitted: { label: 'Menunggu tinjauan', tone: 'warning' },
  revision_requested: { label: 'Perlu revisi', tone: 'warning' },
  approved: { label: 'Disetujui', tone: 'success' },
  auto_approved: { label: 'Disetujui otomatis', tone: 'success' },
  disputed: { label: 'Sengketa', tone: 'danger' },
  released_full: { label: 'Dana cair', tone: 'success' },
  refunded_full: { label: 'Dana dikembalikan', tone: 'info' },
  split: { label: 'Dibagi lewat putusan', tone: 'info' },
}

const contractState: Record<ContractState, Entry> = {
  awaiting_funding: { label: 'Menunggu dana escrow', tone: 'warning' },
  active: { label: 'Berjalan', tone: 'brand' },
  in_dispute: { label: 'Dalam sengketa', tone: 'danger' },
  completed: { label: 'Selesai', tone: 'success' },
  cancelled: { label: 'Dibatalkan', tone: 'neutral' },
}

const payoutState: Record<PayoutState, Entry> = {
  requested: { label: 'Menunggu persetujuan', tone: 'warning' },
  approved: { label: 'Disetujui', tone: 'info' },
  processing: { label: 'Sedang diproses', tone: 'info' },
  completed: { label: 'Selesai', tone: 'success' },
  failed: { label: 'Gagal', tone: 'danger' },
  rejected: { label: 'Ditolak', tone: 'danger' },
}

const proposalStatus: Record<ProposalStatus, Entry> = {
  submitted: { label: 'Terkirim', tone: 'info' },
  shortlisted: { label: 'Masuk seleksi', tone: 'brand' },
  accepted: { label: 'Diterima', tone: 'success' },
  rejected: { label: 'Tidak dipilih', tone: 'neutral' },
  withdrawn: { label: 'Ditarik', tone: 'neutral' },
}

const jobStatus: Record<JobStatus, Entry> = {
  open: { label: 'Terbuka', tone: 'success' },
  closed: { label: 'Ditutup', tone: 'neutral' },
  awarded: { label: 'Sudah berkontrak', tone: 'brand' },
  cancelled: { label: 'Dibatalkan', tone: 'neutral' },
}

const fallback: Entry = { label: 'Tidak diketahui', tone: 'neutral' }

function StatusBadge({ entry }: { entry: Entry }) {
  return <Badge tone={entry.tone}>{entry.label}</Badge>
}

export const CourseStatusBadge = ({ status }: { status: CourseStatus }) => (
  <StatusBadge entry={courseStatus[status] ?? fallback} />
)
export const OrderStatusBadge = ({ state }: { state: OrderState }) => (
  <StatusBadge entry={orderState[state] ?? fallback} />
)
export const MilestoneStatusBadge = ({ state }: { state: MilestoneState }) => (
  <StatusBadge entry={milestoneState[state] ?? fallback} />
)
export const ContractStatusBadge = ({ state }: { state: ContractState }) => (
  <StatusBadge entry={contractState[state] ?? fallback} />
)
export const PayoutStatusBadge = ({ state }: { state: PayoutState }) => (
  <StatusBadge entry={payoutState[state] ?? fallback} />
)
export const ProposalStatusBadge = ({ status }: { status: ProposalStatus }) => (
  <StatusBadge entry={proposalStatus[status] ?? fallback} />
)
export const JobStatusBadge = ({ status }: { status: JobStatus }) => (
  <StatusBadge entry={jobStatus[status] ?? fallback} />
)

export const milestoneLabel = (state: MilestoneState): string => (milestoneState[state] ?? fallback).label
export const contractLabel = (state: ContractState): string => (contractState[state] ?? fallback).label

/** Label ramah untuk tingkat dan format kelas. */
export const levelLabel: Record<string, string> = {
  beginner: 'Pemula',
  intermediate: 'Menengah',
  advanced: 'Lanjutan',
}

export const formatLabel: Record<string, string> = {
  self_paced: 'Belajar mandiri',
  live: 'Kelas live',
  hybrid: 'Mandiri + live',
}

export const methodLabel: Record<string, string> = {
  qris: 'QRIS',
  virtual_account: 'Transfer Virtual Account',
  ewallet: 'Dompet digital',
  card: 'Kartu kredit/debit',
}

export const lessonTypeLabel: Record<string, string> = {
  video: 'Video',
  text: 'Bacaan',
  file: 'Berkas',
  live: 'Sesi live',
  quiz: 'Kuis',
}

// --- Perluasan marketplace: lowongan, jasa, sayembara ----------------------

const vacancyStatus: Record<VacancyStatus, Entry> = {
  draft: { label: 'Draf', tone: 'neutral' },
  open: { label: 'Dibuka', tone: 'success' },
  closed: { label: 'Ditutup', tone: 'neutral' },
  filled: { label: 'Sudah terisi', tone: 'info' },
}

const applicationState: Record<ApplicationState, Entry> = {
  submitted: { label: 'Terkirim', tone: 'info' },
  reviewed: { label: 'Sedang ditinjau', tone: 'info' },
  shortlisted: { label: 'Masuk daftar pendek', tone: 'brand' },
  interview: { label: 'Wawancara', tone: 'brand' },
  offered: { label: 'Ditawari posisi', tone: 'success' },
  hired: { label: 'Diterima', tone: 'success' },
  rejected: { label: 'Tidak dilanjutkan', tone: 'neutral' },
  withdrawn: { label: 'Ditarik', tone: 'neutral' },
}

const serviceStatus: Record<ServiceStatus, Entry> = {
  draft: { label: 'Draf', tone: 'neutral' },
  published: { label: 'Tayang', tone: 'success' },
  paused: { label: 'Dijeda', tone: 'warning' },
  archived: { label: 'Diarsipkan', tone: 'neutral' },
}

const contestStatus: Record<ContestStatus, Entry> = {
  draft: { label: 'Draf', tone: 'neutral' },
  open: { label: 'Menerima karya', tone: 'success' },
  judging: { label: 'Tahap penilaian', tone: 'brand' },
  awarded: { label: 'Pemenang ditetapkan', tone: 'info' },
  cancelled: { label: 'Dibatalkan', tone: 'neutral' },
}

const contestEntryState: Record<ContestEntryState, Entry> = {
  submitted: { label: 'Terkirim', tone: 'info' },
  shortlisted: { label: 'Masuk seleksi', tone: 'brand' },
  winner: { label: 'Pemenang', tone: 'success' },
  rejected: { label: 'Tidak terpilih', tone: 'neutral' },
  withdrawn: { label: 'Ditarik', tone: 'neutral' },
}

export const VacancyStatusBadge = ({ status }: { status: VacancyStatus }) => (
  <StatusBadge entry={vacancyStatus[status] ?? fallback} />
)
export const ApplicationStatusBadge = ({ state }: { state: ApplicationState }) => (
  <StatusBadge entry={applicationState[state] ?? fallback} />
)
export const ServiceStatusBadge = ({ status }: { status: ServiceStatus }) => (
  <StatusBadge entry={serviceStatus[status] ?? fallback} />
)
export const ContestStatusBadge = ({ status }: { status: ContestStatus }) => (
  <StatusBadge entry={contestStatus[status] ?? fallback} />
)
export const ContestEntryBadge = ({ state }: { state: ContestEntryState }) => (
  <StatusBadge entry={contestEntryState[state] ?? fallback} />
)

export const applicationLabel = (state: ApplicationState): string =>
  (applicationState[state] ?? fallback).label

/** Label ramah untuk atribut lowongan kerja. */
export const employmentLabel: Record<string, string> = {
  full_time: 'Penuh waktu',
  part_time: 'Paruh waktu',
  contract: 'Kontrak',
  internship: 'Magang',
  freelance: 'Lepas',
}

export const arrangementLabel: Record<string, string> = {
  onsite: 'Di kantor',
  hybrid: 'Hibrida',
  remote: 'Jarak jauh',
}

export const experienceLabel: Record<string, string> = {
  entry: 'Pemula',
  junior: 'Junior',
  mid: 'Menengah',
  senior: 'Senior',
  lead: 'Lead',
}

export const tierLabel: Record<string, string> = {
  basic: 'Dasar',
  standard: 'Standar',
  premium: 'Premium',
}
